import React, { useMemo } from 'react';

import { schemas } from '@recurrency/core-api-schema';

import { LeadTimeBarChart } from 'components/Charts/LeadTimeBarChart';
import { Container } from 'components/Container';
import { CenteredError, CenteredLoader } from 'components/Loaders';
import { NavTabs } from 'components/NavTabs';
import { NoDataMessage } from 'components/NoDataMessage';
import { PageHeader } from 'components/PageHeader';
import { DetailPageSection, DetailPageSections } from 'components/recipes/detailPage/DetailPageSections';
import { NewPurchaseOrderButton } from 'components/recipes/NewPurchaseOrderButton';
import { NotesHeaderSection } from 'components/recipes/NotesHeaderSection';
import { PurchaseOrderLinesTable } from 'components/recipes/PurchaseOrderLinesTable';
import { PurchaseOrdersTable } from 'components/recipes/PurchaseOrdersTable';
import { VendorItemsTable } from 'components/recipes/VendorItemsTable';

import { useCoreApi } from 'hooks/useApi';

import { truthy } from 'utils/boolean';
import { formatNumber, formatPercent, formatUSD } from 'utils/formatting';
import { IdPathParams, useHashState, usePathParams } from 'utils/routes';

import { DetailsPageWithCompanyIdHashState } from 'types/hash-state';

export const VendorDetailsPage = () => {
  const { id } = usePathParams<IdPathParams>();
  const [hashState] = useHashState<DetailsPageWithCompanyIdHashState>();

  const companyId = hashState?.companyId;
  const { data, isLoading, error } = useCoreApi(schemas.vendors.getVendor, {
    pathParams: { vendorId: id },
    queryParams: { companyId },
  });

  const { data: notes, isLoading: isNotesLoading } = useCoreApi(schemas.vendors.getVendorNotes, {
    pathParams: { vendorId: id },
    queryParams: { companyId },
  });

  const vendorNotes = useMemo(
    () =>
      notes?.items.map((note) => ({
        topic: note.topic,
        note: note.note,
        updatedAt: note.tenantUpdatedAt,
        isCritical: note.isCritical,
      })) ?? [],
    [notes],
  );

  if (error) {
    return <CenteredError error={error} />;
  }

  if (isLoading || !data) {
    return <CenteredLoader />;
  }

  if (Object.keys(data).length === 0) {
    return <NoDataMessage />;
  }

  const { vendorId, vendorName } = data;
  const headerSections: (DetailPageSection | null)[] = [
    {
      title: 'Inventory',
      rows: [
        [
          { label: 'Stockable Item Count', value: formatNumber(data.uniqueItemCount) },
          { label: 'Deadstock %', value: formatPercent(data.deadstockPercent / 100) },
        ],
      ],
    },
    {
      title: 'Purchasing',
      rows: [
        [
          { label: 'Purchases Last 12 Months', value: formatUSD(data.purchasedLast12Months) },
          { label: 'Purchases Year to Date', value: formatUSD(data.purchasesYearToDate) },
          { label: 'Purchase Last Year to Date', value: formatUSD(data.purchasedLastYearToDate) },
        ],
      ],
    },
    {
      title: 'Vendor Notes',
      rows: [
        [
          {
            label: '',
            value: <NotesHeaderSection notes={vendorNotes} />,
            colspan: 3,
            isLoading: isNotesLoading,
          },
        ],
      ],
    },

    {
      title: 'Lead Time (Days)',
      rows: [
        [
          {
            label: '25th Percentile',
            value: formatNumber(data.leadTimeP25),
          },
          {
            label: '50th Percentile',
            value: formatNumber(data.leadTimeP50),
          },
          {
            label: '75th Percentile',
            value: formatNumber(data.leadTimeP75),
          },
          {
            label: '90th Percentile',
            value: formatNumber(data.leadTimeP90),
          },
        ],
      ],
    },
  ];

  return (
    <Container>
      <PageHeader
        title={vendorName}
        copyable
        entity={{ kind: 'Vendor', id: vendorId }}
        headerActions={<NewPurchaseOrderButton />}
      />
      <DetailPageSections sections={headerSections.filter(truthy)} />
      <NavTabs
        tabs={[
          { header: 'Purchase Orders', content: <PurchaseOrdersTable vendorId={vendorId} companyId={companyId} /> },
          {
            header: 'Purchase Order Lines',
            content: <PurchaseOrderLinesTable vendorId={vendorId} companyId={companyId} />,
          },
          { header: 'Items', content: <VendorItemsTable vendorId={vendorId} /> },
          {
            header: 'Lead Times',
            content: <LeadTimeBarChart vendorReportingData={data} />,
          },
        ].filter(truthy)}
      />
    </Container>
  );
};
